import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import {
  ASYNC_FILTER_INPUT_MIN_LENGTH,
  normalizeAsyncFilterSearchText,
  fetchCandidateOptionById,
  fetchCandidateOptionsForSelect,
  fetchFadvCsvCandidateOptionById,
  fetchFadvCsvCandidateOptionsForSelect,
  fetchClientOptionById,
  fetchClientOptionsForSelect,
  fetchClientReportClientOptionById,
  fetchClientReportClientOptionsForSelect,
  fetchInProgressClientOptionById,
  fetchInProgressClientOptionsForSelect,
  fetchSourceOptionById,
  fetchSourceOptionsForSelect,
  fetchUserOptionById,
  fetchUserOptionsForSelect,
} from "helpers/asyncFilterSelectApi";

const selectZIndexStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({
    ...base,
    display: "flex",
    alignItems: "center",
  }),
  valueContainer: (base) => ({
    ...base,
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    height: "100%",
    paddingTop: 0,
    paddingBottom: 0,
    position: "relative",
  }),
  singleValue: (base) => ({
    ...base,
    position: "absolute",
    top: "50%",
    left: 8,
    transform: "translateY(-50%)",
    marginTop: 0,
    marginBottom: 0,
    lineHeight: 1.25,
    maxWidth: "calc(100% - 16px)",
  }),
  placeholder: (base) => ({
    ...base,
    position: "absolute",
    top: "50%",
    left: 8,
    transform: "translateY(-50%)",
    marginTop: 0,
    marginBottom: 0,
    lineHeight: 1.25,
    maxWidth: "calc(100% - 16px)",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
    lineHeight: 1.25,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "100%",
    alignSelf: "stretch",
    alignItems: "center",
  }),
};

/** On menu open, preload clients with `nameContains: null` for these roles. */
const ASYNC_CLIENT_PRELOAD_USER_TYPE_IDS = new Set([5, 8, 14]);

const resolveLoggedInUserTypeIdForClientSelect = () => {
  try {
    const raw = localStorage.getItem("userData");
    if (!raw) return null;
    const user = JSON.parse(raw);
    if (!user || typeof user !== "object") return null;
    const nested = user.user && typeof user.user === "object" ? user.user : null;
    const id = Number(
      user.userTypeId ??
        user.UserTypeId ??
        nested?.userTypeId ??
        nested?.UserTypeId
    );
    return Number.isFinite(id) ? id : null;
  } catch (_e) {
    return null;
  }
};

const KIND_CONFIG = {
  client: {
    fetchSearch: fetchClientOptionsForSelect,
    fetchById: fetchClientOptionById,
    defaultPlaceholder: "Select Client",
    minInputLength: 1,
    emptyHint: "Type at least 2 character to search client",
  },
  inProgressClient: {
    fetchSearch: fetchInProgressClientOptionsForSelect,
    fetchById: fetchInProgressClientOptionById,
    defaultPlaceholder: "Select Client",
    minInputLength: 1,
    emptyHint: "Type at least 2 character to search client",
  },
  clientReportClient: {
    fetchSearch: fetchClientReportClientOptionsForSelect,
    fetchById: fetchClientReportClientOptionById,
    defaultPlaceholder: "Select Client",
    minInputLength: 1,
    emptyHint: "Type at least 2 character to search client",
  },
  source: {
    fetchSearch: fetchSourceOptionsForSelect,
    fetchById: fetchSourceOptionById,
    defaultPlaceholder: "Select Source",
    minInputLength: 1,
    emptyHint: "Type at least 2 character to search source",
  },
  user: {
    fetchSearch: fetchUserOptionsForSelect,
    fetchById: fetchUserOptionById,
    defaultPlaceholder: "Select User",
    minInputLength: 0,
    emptyHint: "write something to search users",
  },
  candidate: {
    fetchSearch: (searchText, context = {}) =>
      fetchCandidateOptionsForSelect(searchText, context?.clientId),
    fetchById: fetchCandidateOptionById,
    defaultPlaceholder: "Select Candidate",
    emptyHint: "Type at least 4 characters to search candidates",
    requiresClientId: true,
  },
  fadvCsvCandidate: {
    fetchSearch: fetchFadvCsvCandidateOptionsForSelect,
    fetchById: fetchFadvCsvCandidateOptionById,
    defaultPlaceholder: "Select Candidate",
    emptyHint: "Type at least 4 characters to search candidates",
    requiresClientId: false,
  },
};

const mergeOptionLists = (sticky, menuList) => {
  const map = new Map();
  if (sticky?.value) map.set(String(sticky.value), sticky);
  (Array.isArray(menuList) ? menuList : []).forEach((o) => {
    if (o?.value) map.set(String(o.value), o);
  });
  return Array.from(map.values());
};

/**
 * Async react-select — options load after min input length (default 4 chars; clients: 2 chars; users: 1 char).
 * Selected value is kept visible via optional fetch-by-id when `value` is set but not in menu.
 * Optional `initialOption` ({ value, label }) pre-seeds the label when it matches `value` (e.g. edit forms with id+name from the row).
 */
const AsyncSearchSelect = ({
  kind = "client",
  value = "",
  onChange,
  placeholder,
  isDisabled = false,
  isClearable = true,
  initialOption = null,
  className = "select2-selection",
  classNamePrefix = "select2",
  /** Required for `kind="candidate"` — scopes search to one client. */
  clientId = "",
  /** Required for `kind="user"` filter selects — POST `common/system-users` `pageName`. */
  pageName = "",
  /** Restart onboarding only — forces V2 client `pageName` `OnboardingAdd` (ignores bag body). */
  restartClientBag = null,
  /** Override kind default (e.g. `0` → search after 1 character). */
  minInputLength: minInputLengthProp,
  emptyHint: emptyHintProp,
}) => {
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.client;
  const isSourceSelect = kind === "source";
  const isClientSelect = kind === "client";
  const shouldPreloadClientOptionsOnOpen =
    isClientSelect &&
    ASYNC_CLIENT_PRELOAD_USER_TYPE_IDS.has(
      resolveLoggedInUserTypeIdForClientSelect()
    );
  const minInputLength =
    minInputLengthProp ?? cfg.minInputLength ?? ASYNC_FILTER_INPUT_MIN_LENGTH;
  const emptyHint = emptyHintProp ?? cfg.emptyHint;
  const scopedClientId = String(clientId ?? "").trim();
  const resolvedPageName = String(pageName ?? "").trim();
  const fetchContext = useMemo(() => {
    const context = { clientId: scopedClientId };
    if (resolvedPageName) {
      context.pageName = resolvedPageName;
    }
    if (restartClientBag && typeof restartClientBag === "object") {
      context.restartClientBag = restartClientBag;
    }
    return context;
  }, [resolvedPageName, restartClientBag, scopedClientId]);
  const candidateNeedsClient = cfg.requiresClientId && !scopedClientId;
  const [menuOptions, setMenuOptions] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [stickyOption, setStickyOption] = useState(null);
  const fetchSeq = useRef(0);

  const stringValue = String(value ?? "").trim();
  const initialOptionValue = String(initialOption?.value ?? "").trim();
  const initialOptionLabel = String(initialOption?.label ?? "").trim();

  const applyStickyOption = useCallback((nextOption) => {
    setStickyOption((previous) => {
      const nextValue = String(nextOption?.value ?? "").trim();
      const nextLabel = String(nextOption?.label ?? "").trim();
      const previousValue = String(previous?.value ?? "").trim();
      const previousLabel = String(previous?.label ?? "").trim();
      if (!nextValue) {
        return previous === null ? previous : null;
      }
      if (previousValue === nextValue && previousLabel === nextLabel) {
        return previous;
      }
      return { value: nextValue, label: nextLabel };
    });
  }, []);

  useEffect(() => {
    const cfgLocal = KIND_CONFIG[kind] || KIND_CONFIG.client;
    if (!stringValue) {
      applyStickyOption(null);
      return;
    }

    const fromInitial =
      initialOptionValue === stringValue && Boolean(initialOptionLabel);
    if (fromInitial) {
      applyStickyOption({
        value: stringValue,
        label: initialOptionLabel,
      });
      return;
    }

    let cancelled = false;
    (async () => {
      const opt = await cfgLocal.fetchById(stringValue, fetchContext);
      if (cancelled) return;
      if (opt) {
        applyStickyOption(opt);
      } else {
        applyStickyOption({ value: stringValue, label: stringValue });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    stringValue,
    kind,
    initialOptionValue,
    initialOptionLabel,
    resolvedPageName,
    scopedClientId,
    fetchContext,
    applyStickyOption,
  ]);

  const mergedOptions = useMemo(
    () => mergeOptionLists(stickyOption, menuOptions),
    [stickyOption, menuOptions]
  );

  const selectValue =
    mergedOptions.find((o) => String(o?.value) === stringValue) ||
    (stringValue && stickyOption && String(stickyOption.value) === stringValue ? stickyOption : null);

  const handleChange = useCallback(
    (opt) => {
      setStickyOption(opt || null);
      setMenuOptions([]);
      onChange?.(opt || null);
    },
    [onChange]
  );

  const runMenuSearch = useCallback(
    (searchText) => {
      const seq = ++fetchSeq.current;
      setMenuLoading(true);
      cfg
        .fetchSearch(searchText, fetchContext)
        .then((opts) => {
          if (fetchSeq.current === seq) setMenuOptions(Array.isArray(opts) ? opts : []);
        })
        .catch(() => {
          if (fetchSeq.current === seq) setMenuOptions([]);
        })
        .finally(() => {
          if (fetchSeq.current === seq) setMenuLoading(false);
        });
    },
    [cfg, fetchContext]
  );

  const handleMenuOpen = useCallback(() => {
    if (!shouldPreloadClientOptionsOnOpen || candidateNeedsClient) return;
    runMenuSearch("");
  }, [
    candidateNeedsClient,
    runMenuSearch,
    shouldPreloadClientOptionsOnOpen,
  ]);

  const handleInputChange = useCallback(
    (inputValue, meta) => {
      if (meta.action !== "input-change") return;
      const t = isSourceSelect
        ? normalizeAsyncFilterSearchText(inputValue)
        : String(inputValue || "").trim();
      if (t.length > minInputLength && !candidateNeedsClient) {
        runMenuSearch(isSourceSelect ? inputValue : t);
      } else if (
        shouldPreloadClientOptionsOnOpen &&
        !t &&
        !candidateNeedsClient
      ) {
        runMenuSearch("");
      } else {
        fetchSeq.current += 1;
        setMenuOptions([]);
        setMenuLoading(false);
      }
    },
    [
      candidateNeedsClient,
      isSourceSelect,
      minInputLength,
      runMenuSearch,
      shouldPreloadClientOptionsOnOpen,
    ]
  );

  const handleSourceKeyDown = useCallback((event) => {
    if (event.key !== " ") return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.value) {
      event.stopPropagation();
    }
  }, []);

  return (
    <Select
      className={className}
      classNamePrefix={classNamePrefix}
      options={mergedOptions}
      value={selectValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onMenuOpen={shouldPreloadClientOptionsOnOpen ? handleMenuOpen : undefined}
      {...(isSourceSelect
        ? {
            filterOption: () => true,
            hideSelectedOptions: false,
            onKeyDown: handleSourceKeyDown,
          }
        : {})}
      placeholder={placeholder || cfg.defaultPlaceholder}
      isClearable={isClearable}
      isDisabled={isDisabled || candidateNeedsClient}
      isSearchable
      isLoading={menuLoading}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      styles={selectZIndexStyles}
      noOptionsMessage={({ inputValue }) => {
        if (candidateNeedsClient) return "Select a client first";
        const t = isSourceSelect
          ? normalizeAsyncFilterSearchText(inputValue)
          : String(inputValue || "").trim();
        if (shouldPreloadClientOptionsOnOpen && !t) {
          return menuLoading ? "Loading..." : "No results found";
        }
        if (t.length <= minInputLength) return emptyHint;
        return "No results found";
      }}
    />
  );
};

/** Pre-loaded options — same menu portal / z-index as AsyncSearchSelect. */
export const StaticFilterSelect = ({
  options = [],
  value = "",
  onChange,
  placeholder = "Select",
  isDisabled = false,
  isClearable = true,
  isSearchable = true,
  className = "select2-selection",
  classNamePrefix = "select2",
}) => {
  const stringValue = String(value ?? "").trim();
  const selectValue =
    (Array.isArray(options) ? options : []).find(
      (option) => String(option?.value ?? "") === stringValue
    ) || null;

  return (
    <Select
      className={className}
      classNamePrefix={classNamePrefix}
      options={options}
      value={selectValue}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      styles={selectZIndexStyles}
    />
  );
};

export const AsyncClientSelect = (props) => <AsyncSearchSelect kind="client" {...props} />;

export const AsyncInProgressClientSelect = (props) => (
  <AsyncSearchSelect kind="inProgressClient" {...props} />
);

export const AsyncClientReportClientSelect = (props) => (
  <AsyncSearchSelect kind="clientReportClient" {...props} />
);

export const AsyncSourceSelect = (props) => <AsyncSearchSelect kind="source" {...props} />;

export const AsyncUserSelect = (props) => <AsyncSearchSelect kind="user" {...props} />;

/**
 * Async multi user select — POST `common/system-users` `{ pageName, search }`.
 * `value` is `[{ value, label }, …]` so edit forms can preselect id + name from the row.
 */
export const AsyncUserMultiSelect = ({
  value = [],
  onChange,
  pageName = "",
  placeholder = "Select Default User",
  isDisabled = false,
  className = "select2-selection",
  classNamePrefix = "select2",
}) => {
  const resolvedPageName = String(pageName ?? "").trim();
  const selectedOptions = useMemo(
    () =>
      (Array.isArray(value) ? value : [])
        .map((opt) => {
          const optionValue = String(opt?.value ?? "").trim();
          if (!optionValue) return null;
          const label = String(opt?.label ?? "").trim() || optionValue;
          return { value: optionValue, label };
        })
        .filter(Boolean),
    [value]
  );
  const [menuOptions, setMenuOptions] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const fetchSeq = useRef(0);

  const mergedOptions = useMemo(
    () => mergeOptionLists(null, [...selectedOptions, ...menuOptions]),
    [selectedOptions, menuOptions]
  );

  const handleChange = useCallback(
    (opts) => {
      const next = Array.isArray(opts) ? opts : [];
      setMenuOptions([]);
      onChange?.(next);
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (inputValue, meta) => {
      if (meta.action !== "input-change") return;
      const searchText = String(inputValue || "").trim();
      if (searchText && resolvedPageName) {
        const seq = ++fetchSeq.current;
        setMenuLoading(true);
        fetchUserOptionsForSelect(searchText, { pageName: resolvedPageName })
          .then((opts) => {
            if (fetchSeq.current === seq) setMenuOptions(Array.isArray(opts) ? opts : []);
          })
          .catch(() => {
            if (fetchSeq.current === seq) setMenuOptions([]);
          })
          .finally(() => {
            if (fetchSeq.current === seq) setMenuLoading(false);
          });
      } else {
        fetchSeq.current += 1;
        setMenuOptions([]);
        setMenuLoading(false);
      }
    },
    [resolvedPageName]
  );

  return (
    <Select
      isMulti
      className={className}
      classNamePrefix={classNamePrefix}
      options={mergedOptions}
      value={selectedOptions}
      getOptionValue={(opt) => String(opt?.value ?? "")}
      getOptionLabel={(opt) => String(opt?.label ?? "")}
      onChange={handleChange}
      onInputChange={handleInputChange}
      placeholder={placeholder}
      isDisabled={isDisabled || !resolvedPageName}
      isSearchable
      isLoading={menuLoading}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      styles={selectZIndexStyles}
      noOptionsMessage={({ inputValue }) => {
        const t = String(inputValue || "").trim();
        if (!resolvedPageName) return "Page name is required";
        if (!t) return "Type to search users";
        return "No results found";
      }}
    />
  );
};

export const AsyncCandidateSelect = (props) => (
  <AsyncSearchSelect kind="candidate" {...props} />
);

export const AsyncAutocompleteCandidate = (props) => (
  <AsyncSearchSelect kind="fadvCsvCandidate" {...props} />
);
